import React, { memo, useEffect, useMemo, useState } from 'react';

import {
  Box,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Stack,
  Center,
  HStack,
} from '@chakra-ui/react';
import cloneDeep from 'lodash/cloneDeep';
import merge from 'lodash/merge';
import { customTheme } from 'theme';

import { getIcon } from 'components/Icons/Components';

import { defaultStyles, selectedTabStyles } from './styles';
import { CustomTabsProps } from './types';

export const CustomTabs = memo((props: CustomTabsProps) => {
  const {
    items,
    styles,
    overwriteDefaultStyles,
    defaultIndex,
    currentIndex,
    componentHeader,
    onChange,
    className,
    rightComponent,
  } = props;

  const [currentTabIndex, setCurrentTabIndex] = useState(
    defaultIndex ?? currentIndex ?? 0,
  );

  useEffect(() => {
    setCurrentTabIndex(defaultIndex ?? currentIndex ?? 0);
  }, [currentIndex, defaultIndex]);

  const baseStyles = useMemo(() => {
    const props = cloneDeep(defaultStyles);
    return overwriteDefaultStyles ? styles : merge(props, styles);
  }, [styles, overwriteDefaultStyles]);

  const handleOnchange = (index: number) => {
    onChange?.(index);
    setCurrentTabIndex(index);
  };

  return (
    <Tabs
      isLazy
      {...baseStyles?.tabs}
      index={currentTabIndex}
      defaultIndex={defaultIndex}
      onChange={handleOnchange}
    >
      <TabList {...baseStyles?.tabList} pos={'relative'} className={className}>
        <HStack justify="space-between" w="full">
          <HStack spacing={0}>
            {items.map((each, index) => (
              <Tab
                key={`${each.key}-tab`}
                {...baseStyles?.tab}
                isDisabled={each.isDisabled}
              >
                <Stack spacing={0}>
                  <Stack
                    spacing={1}
                    {...styles?.tab?.stack}
                    px={3}
                    pt={1.5}
                    pb={1.5}
                    {...(index === currentTabIndex ? selectedTabStyles : {})}
                  >
                    {(each?.icon || each?.iconType) && (
                      <Center color={each?.styles?.iconColor ?? 'primary'}>
                        <Box boxSize={each?.styles?.iconSize ?? 5}>
                          {!!each?.iconType
                            ? getIcon(each?.iconType)
                            : each?.icon}
                        </Box>
                      </Center>
                    )}
                    <Center>{each.title}</Center>
                  </Stack>
                  <Box px={3}>
                    <Box
                      //h="3px"
                      {...(index === currentTabIndex
                        ? {
                            borderBottom: '3px solid',
                            borderColor: customTheme.colors.primary,
                            //boxShadow: `inset 0 -3px ${customTheme.colors.primary}`,
                          }
                        : {})}
                    />
                  </Box>
                </Stack>
              </Tab>
            ))}
          </HStack>
          {rightComponent && <Box>{rightComponent}</Box>}
        </HStack>
        {componentHeader}
      </TabList>

      <TabPanels flex={1} overflowY="auto" {...baseStyles?.tabPanels}>
        {props.items.map(each => (
          <TabPanel
            key={`${each.key}-tab-panel`}
            pos={'relative'}
            {...baseStyles?.tabPanel}
            h={'full'}
          >
            <Box h={'full'}>{each.component}</Box>
          </TabPanel>
        ))}
      </TabPanels>
    </Tabs>
  );
});
